import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { editCustomerbaseFields, getCargosPlaces, getCountries, getCustomerBaseById, getMobilePrefix, getProvinces, getSapEnumConfiguration } from "../api/service/customerbaseApi";
import CustomerBaseModal from "../component/modal/CustomerBaseModal";
import { cargosPlacesState, cbContactPersonToDeleteArrayState, cbErrorSectionValidationState, countriesState, customerSearchFiltersState, defaultBuagClassesState, defaultSalesAreasState, isGlobalEditModeState, isMobilityVoucherModeState, mobilePrefixState, openCustomerBaseModal, provincesState, queryFieldsState, sapEnumState } from "../state/customerbaseState";
import {
    CBAddressType,
    CBCountry,
    CBLegalFormKey,
    CBOmissionType,
    CBPartnerType,
    CBPaymentMethod,
    CBRoleKey, CBSapCode,
    CBValuableLength,
    CBYesNoType,
    CBYupTestID,
    CustomerBaseDto,
    IFormValues,
    IGetCargosPlaceReturn,
    IGetProvincesReturn,
    NOT_REQUIRED_LEGAL_FORMS, VALID_GENERIC_FORMAT_IBAN_REGEX, VALID_IT_SM_FORMAT_IBAN_REGEX,
    UPDATE_SUCCESS_WITH_EXTRA_ADDRESS_PROBLEM_CODE,
    CBCreationOfProps,
    CBInvoicingCode,
    IT_FISCAL_CODE_FORMAT_REGEX,
    VALID_SIRET_FORMAT_REGEX,
    ES_FISCAL_CODE_FORMAT_REGEX,
    PT_FISCAL_CODE_FORMAT_REGEX
} from "../types";
import * as yup from "yup";
import ActionsBar from "../component/ActionsBar";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import CustomLoader from "../../../util/component/CustomLoader";
import CustomerBaseTabsManager from "../component/CustomerbaseTabsManager";
import { getErrorKey, getInvoicingAddress, getResidenceAddress, getSalesOrganizationKeyByTenants, isTaxCodeFormatValid, isVatNumberValid, getCountriesByTenants, getCompanyCodeByTenant } from "../utils";
import CBSectionHeader from "../component/sectionHeader/CBSectionHeader";

interface ViewCustomerBaseProps extends CBCreationOfProps {
    onCustomerSubmit?: (customer: any) => void
}

const ViewCustomerBase = ({ onCustomerSubmit, creationOf }: ViewCustomerBaseProps) => {

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);
    const {t} = useTranslation("customerbase");
    const { id: cbId } = useParams();
    const navigate = useNavigate();
    const isCreationMode = !!creationOf;
    const isPersonCreation = (creationOf == "person");
    const isOrganizationCreation = (creationOf == "organization");
    const [currentCB, setCurrentCB] = useState<CustomerBaseDto | null>(null);
    const openModal = useRecoilValue(openCustomerBaseModal);
    const setOpenModal = useSetRecoilState(openCustomerBaseModal);
    const sapEnum = useRecoilValue(sapEnumState);
    const setSapEnum = useSetRecoilState(sapEnumState);
    const setCustomerSearchFilters = useSetRecoilState(customerSearchFiltersState);
    const queryFields = useRecoilValue(queryFieldsState);
    const setQueryFields = useSetRecoilState(queryFieldsState);
    const mobilePrefixes = useRecoilValue(mobilePrefixState);
    const setMobilePrefix = useSetRecoilState(mobilePrefixState);
    const countryOptions = useRecoilValue(countriesState);
    const setCountryOptions = useSetRecoilState(countriesState);
    const provinceOptions = useRecoilValue(provincesState);
    const setProvinceOptions = useSetRecoilState(provincesState);
    const cargosPlaceOptions = useRecoilValue(cargosPlacesState);
    const setCargosPlaceOptions = useSetRecoilState(cargosPlacesState);
    const setDefaultSalesAreas = useSetRecoilState(defaultSalesAreasState);
    const setDefaultBuagClasses = useSetRecoilState(defaultBuagClassesState);

    const setIsGlobalEditMode = useSetRecoilState(isGlobalEditModeState);
    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);
    const cbContactPersonToDeleteArray = useRecoilValue(cbContactPersonToDeleteArrayState);
    const setCbContactPersonToDeleteArray = useSetRecoilState(cbContactPersonToDeleteArrayState);

    const [isMobilityVoucherMode, setIsMobilityVoucherMode] = useRecoilState(isMobilityVoucherModeState);

    const handleYupTransform = (value: any) => {
        return (value == CBOmissionType.OMISSION_DASH) ? '' : value;
    }
    const validationSchema = isCreationMode ? 
        yup.object().shape({
            cbDetails: yup.object().shape({
                mobilePrefix: yup.string().required(t("message.validation.requiredField")),
                mobile: yup.string().required(t("message.validation.requiredField")),
                email: yup.string().required(t("message.validation.requiredField")).trim().lowercase().email(t("message.validation.emailNotValid")),
                language: yup.string().required(t("message.validation.requiredField")),
                role: yup.array().min(1, t("message.validation.requiredField")),
                /*legalForm: yup.string().when([], {
                    is: () => isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField"))
                })*/
            }),
            cbPersonalInfo: yup.object().shape({
                firstName: yup.string().when([],{
                    is: () => !isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().notRequired()

                }),
                lastName: yup.string().when([],{
                    is: () => !isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().notRequired()

                }),
                birthDate: yup.string().when([],{
                    is: () => !isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().notRequired()

                }),
                businessName: yup.string().when([],{
                    is: () => isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().notRequired()

                }),
                vatNumberCountry: yup.string().nullable().when(['residenceCountry'], {
                    is: (residenceCountry: string) => (!!form.getValues('cbDetails.cbCustomerId') && residenceCountry == CBCountry.ITALY && isOrganizationCreation && !NOT_REQUIRED_LEGAL_FORMS.includes(form.getValues("cbPersonalInfo.legalForm") || '')) || (!form.getValues('cbDetails.cbCustomerId') && isOrganizationCreation && !NOT_REQUIRED_LEGAL_FORMS.includes(form.getValues("cbPersonalInfo.legalForm") || '')),
                    then: yup.string().nullable().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().nullable().notRequired()
                }),
                vatNumber: yup.string().when([], {
                    is: () => isOrganizationCreation && !NOT_REQUIRED_LEGAL_FORMS.includes(form.getValues("cbPersonalInfo.legalForm") || ''),
                    then: yup.string().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().notRequired().trim()
                }).test(
                    CBYupTestID.CHECK_VAT_NUMBER,
                    (value, ctx) => {
                        if(isOrganizationCreation && !!value) {
                            if(ctx.parent?.vatNumberCountry == CBCountry.ITALY)
                                return isVatNumberValid(value, CBCountry.ITALY) ? true : ctx.createError({message: t("message.validation.exactly11Numbers")});
                            
                            return isVatNumberValid(value) ? true : ctx.createError({message: t("message.validation.min5Chars")});
                        }

                        return true;
                    }
                ),
                legalForm: yup.string().when([], {
                    is: () => isOrganizationCreation,
                    then: yup.string().required(t("message.validation.requiredField"))
                }),
                publicAuthority: yup.string().required(t("message.validation.requiredField")),
                paIdNumber: yup.string().when(['publicAuthority'], {
                    is: (publicAuthority: string) => isOrganizationCreation && publicAuthority == CBYesNoType.YES,
                    then: yup.string().required(t("message.validation.requiredField")).min(6, t("message.validation.exactly6Chars"))
                }),
                //birthCountry: yup.string().notRequired().trim(),
                /*residenceCountry: yup.string().nullable().when([], {
                    is: () => isPerson,
                    then: yup.string().nullable().required(t("message.validation.requiredField")).trim(),
                    otherwise: yup.string().nullable().notRequired()
                }),*/
                residenceCountry: yup.string().nullable().required(t("message.validation.requiredField")).trim(),
                residenceProvince: yup.string().when(["residenceCountry"], {
                    is: (residenceCountry: string) => !!residenceCountry && residenceCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField"))
                }),
                residenceCity: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                residenceZipCode: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                residenceStreet: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                residenceHouseNumber: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                taxCode: yup.string().trim().uppercase()
                    .test(
                        CBYupTestID.IS_TAX_CODE_REQUIRED_ON_CREATE,
                        t("message.validation.requiredField"),
                        (value, ctx) => {
                            return [CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL].includes(ctx?.parent?.residenceCountry) ? !!value : true;
                        }
                    )
                    .test(
                        CBYupTestID.IS_TAX_CODE_FORMAT_VALID_FOR_PERSON_ON_CREATE,
                        t("message.validation.formatNotValid"),
                        (value, ctx) => {
                            return isPersonCreation && !!value ? isTaxCodeFormatValid(value, ctx?.parent?.residenceCountry) : true;
                        }
                    )
                    .test(
                        CBYupTestID.TAX_CODE_FORMAT_NOT_VALID_FOR_ORGANIZATION_ON_CREATE, 
                        (value, ctx) => {
                            if(isPerson) {
                                const residenceCountry = ctx?.parent?.residenceCountry
                                if(!!residenceCountry && ![CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL, CBOmissionType.OMISSION_DASH].includes(residenceCountry)
                                    && !!value && value !== CBOmissionType.OMISSION_DASH) {
                                        return isTaxCodeFormatValid(value, residenceCountry) ? true : ctx.createError({message: t("message.validation.formatNotValid")});
                                }
                            } else {
                                const residenceCountry = ctx?.parent?.residenceCountry
                                if([CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL].includes(residenceCountry) && !value)
                                    return ctx.createError({message: t("message.validation.requiredField")});

                                if(ctx.parent.legalForm == CBLegalFormKey.DITTA_INDIVIDUALE) {
                                    return isTaxCodeFormatValid(value || null, residenceCountry) ? true : ctx.createError({message: t("message.validation.formatNotValid")});
                                }

                                /*if(value !== CBOmissionType.OMISSION_DASH)
                                    return value.length >= CBValuableLength.MIN_VAT_NUMBER ? true : ctx.createError({message: t("message.validation.min5Chars")});*/
                            }

                            return true;
                        }
                    ),
                /*birthProvince: yup.string().when(['birthCountry'], {
                    is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                    then: yup.string().trim().required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired().trim()
                }),
                birthCity: yup.string().when(['birthCountry'], {
                    is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                    then: yup.string().trim().required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired().trim()
                }),
                gender: yup.string().when(['birthCountry'], {
                    is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                    then: yup.string().trim().required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired().trim()
                }),*/
                gender: yup.string().when([], {
                    is: () => isPerson,
                    then: yup.string().trim().required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired().trim()
                }),
                drvLicenceNumber: yup.string().when(['residenceCountry'], {
                    // Patente obbligatoria solo per paesi non-IT, non-ES, non-PT (per ES/PT il NIF è già obbligatorio)
                    is: (residenceCountry: string) => isPerson && !!residenceCountry && ![CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL].includes(residenceCountry as CBCountry),
                    then: yup.string().trim().required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired().trim()
                }),
                siren: yup.string().when(["vatNumberCountry"], {
                    is: (vatNumberCountry: string) => isOrganizationCreation && !!vatNumberCountry && vatNumberCountry == CBCountry.FRANCE,
                    then: yup.string().required(t("message.validation.requiredField")).trim().min(CBValuableLength.SIREN, t("message.validation.exactly9Chars")).max(CBValuableLength.SIREN, t("message.validation.exactly9Chars")),
                    otherwise: yup.string().notRequired().trim()
                }),
                siret: yup.string().when(["vatNumberCountry"], {
                    is: (vatNumberCountry: string) => isOrganizationCreation && !!vatNumberCountry && vatNumberCountry == CBCountry.FRANCE,
                    then: yup.string().notRequired().trim().matches(VALID_SIRET_FORMAT_REGEX, {excludeEmptyString: true, message: t("message.validation.exactly14Chars")}),
                    otherwise: yup.string().notRequired().trim()
                })
                .test(
                    CBYupTestID.IS_SIRET_AND_SIREN_COMPLIANT_ON_CREATE,
                    t("message.validation.sirenAndSiretNotCompliant"),
                    (value, ctx) => {
                        return (isOrganizationCreation && !!ctx.parent.vatNumberCountry && ctx.parent.vatNumberCountry == CBCountry.FRANCE && !!value && !!ctx.parent.siren && value.length == CBValuableLength.SIRET && ctx.parent.siren.length == CBValuableLength.SIREN) ?
                            value.slice(0,CBValuableLength.SIREN) == ctx.parent.siren : true;
                    }
                )
            }),
            cbInvoicingAddress: yup.object().shape({
                invoicingStreet: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                invoicingHouseNumber: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                invoicingAddrEmail: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim().lowercase().email(t("message.validation.emailNotValid")),
                    otherwise: yup.string().notRequired().trim().lowercase().email(t("message.validation.emailNotValid"))
                }),
                invoicingZipCode: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                invoicingCountry: yup.string().nullable().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().nullable().required(t("message.validation.requiredField"))
                }),
                invoicingProvince: yup.string().when(["invoicingCountry"], {
                    is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField"))
                }),
                invoicingCity: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().required(t("message.validation.requiredField")).trim()
                }),
                invoicingPec: yup.string().when(["invoicingCountry"], {
                    is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY,
                    then: yup.string().notRequired().trim().lowercase().email(t("message.validation.emailNotValid")),
                    otherwise: yup.string().notRequired().trim().lowercase()
                }),
                invoicingRecipientCode: yup.string().when(["invoicingCountry"], {
                    is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && (form.getValues("cbPersonalInfo.publicAuthority") == CBYesNoType.NO),
                    then: yup.string().required(t("message.validation.requiredField")).trim().min(7, t("message.validation.exactly7Chars")),
                    otherwise: yup.string().notRequired().trim()
                })
            }),
            cbPaymentInfo: yup.array().of(
                yup.object().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.object().shape({
                        buagClass: yup.string().required(t("message.validation.requiredField")),
                        paymentMethod: yup.string().required(t("message.validation.requiredField")),
                        paymentTerms:  yup.string().required(t("message.validation.requiredField")),
                        correspondingVariant: yup.string().required(t("message.validation.requiredField")),
                        iban: yup.string().when(['paymentMethod'], {
                            is: (paymentMethod: string) => paymentMethod == CBPaymentMethod.DIRECT_DEBIT,
                            then: yup.string().required(t("message.validation.requiredField")),
                            otherwise: yup.string().notRequired().trim()
                        })
                    }),
                    otherwise: yup.object().shape({
                        paymentMethod: yup.string().notRequired(),
                        iban: yup.string().when(['paymentMethod'], {
                            is: (paymentMethod: string) => paymentMethod == CBPaymentMethod.DIRECT_DEBIT,
                            then: yup.string().required(t("message.validation.requiredField")),
                            otherwise: yup.string().notRequired().trim()
                        })
                    })
                })
            ).test(
                CBYupTestID.CHECK_FORMAT_IBAN,
                (value, ctx) => {
                    if(!!value) {
                        var err: any[] = [];
                        value.forEach((el, index) => {

                            var iban: string = el.iban;
                            if(!!iban) {
                                if(iban.length > 3) {
                                    iban = iban.toUpperCase();
                                    const countryCode = iban.substring(0,2);

                                    if(countryCode == CBCountry.ITALY || countryCode == CBCountry.SAN_MARINO) {
                                        if(!iban.match(VALID_IT_SM_FORMAT_IBAN_REGEX))
                                            err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));
                                    }
                                    else if(!iban.match(VALID_GENERIC_FORMAT_IBAN_REGEX))
                                        err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));

                                }
                                else err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));
                            }

                        });

                        return err.length > 0 ? ctx.createError({message: () => err}) : true;
                    }
                    return true;
                }
            ).test(
                CBYupTestID.AT_LEAST_ONE_PAYMENT_REQUIRED,
                (value) => {
                    if(form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && value?.length == 0) {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('message.validation.atLeastOnePaymentRequired'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoclose: true
                                }
                            }
                        });

                        return false;
                    }
                    return true;
                }
            ).test(
                CBYupTestID.BUAG_SALES_AREA_MARKET_CONSISTENCY_ON_CREATE,
                (value, ctx) => {
                    if(form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && !!value && (value?.length > 0) && (ctx.parent?.salesAreas?.length > 0)) {

                        const buagClasses: string[] = value.map(p => p?.buagClass);
                        const salesOrganizations: string[] = ctx.parent?.salesAreas?.map((sa: any) => sa?.salesOrganization);

                        let soUnique: string[] = [];
                        salesOrganizations.forEach(so => {
                            if(!soUnique.includes(so))
                                soUnique.push(so);
                        })

                        const bcFiltered = buagClasses.filter(bc => soUnique.includes(bc));
                        let bcfUnique: string[] = [];
                        bcFiltered.forEach(bcf => {
                            if(!bcfUnique.includes(bcf))
                                bcfUnique.push(bcf);
                        })

                        if(bcfUnique.length != soUnique.length) {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('message.validation.buagSalesAreaMarketConsistency'),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoclose: true
                                    }
                                }
                            });

                            return false;
                        }
                    }

                    return true;
                }
            ),
            salesAreas: yup.array().of(
                yup.object().shape({
                    salesOrganization: yup.string().required(t("message.validation.requiredField")),
                    distributionChannel: yup.string().required(t("message.validation.requiredField")),
                    division: yup.string().required(t("message.validation.requiredField")),
                    currency: yup.string().required(t("message.validation.requiredField"))
                })
            ).test(
                CBYupTestID.IS_SALES_ORGANIZATION_ALLOWED_UNDER_TENANTS,
                (value, ctx) => {
                    if(!form.getValues('cbDetails.cbCustomerId') && !!value) {

                        var err: any[] = [];
                        const validMarketsByTenants = getCountriesByTenants(globalData?.user?.legalEntities);

                        value.forEach((el, index) => {
                            if(!!el.salesOrganization && !validMarketsByTenants.includes(el.salesOrganization)) {
                                err.push(new yup.ValidationError(t("message.validation.valueNotPermitted"), value[index].salesOrganization,`${ctx.path}[${index}].salesOrganization`));
                            }
                        });

                        return err.length > 0 ? ctx.createError({message: () => err}) : true;
                    }

                    return true;
                }
            ),
            cbCorporateInfo: yup.object().when([], {
                is: () => !isPersonCreation,
                then: yup.object().shape({
                        contactPersons: yup.array().of(yup.object().shape({
                            name: yup.string().required(t("message.validation.requiredField")).trim().max(CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH, t("message.validation.max20Chars")),
                            surname: yup.string().required(t("message.validation.requiredField")).trim().max(CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH, t("message.validation.max20Chars")),
                            email: yup.string().notRequired().trim().lowercase().email(t("message.validation.emailNotValid"))
                        })).test(
                            CBYupTestID.AT_LEAST_ONE_CONTACT_PERSON_REQUIRED,
                            (value) => {
                                if(value?.length == 0) {
                                    dispatchGlobalData({
                                        type: setSnackbar,
                                        payload: {
                                            snackbar: {
                                                message: t('message.validation.atLeastOneContactPersonRequired'),
                                                type: snackbarTypes.error,
                                                show: true,
                                                autoclose: true
                                            }
                                        }
                                    });
            
                                    return false;
                                }
                                return true;
                            })
                    })
            })
        })
    : 
    yup.object().shape({
        cbDetails: yup.object({
            email: yup.string().required(t("message.validation.requiredField")).trim().lowercase().email(t("message.validation.emailNotValid")),
            mobilePrefix: yup.string().required(t("message.validation.requiredField")),
            mobile: yup.string().trim().required(t("message.validation.requiredField")),
            language: yup.string().trim().required(t("message.validation.requiredField")),
            role: yup.array().min(1, t("message.validation.requiredField")),
            /*legalForm: yup.string().when([], {
                is: () => !isPerson,
                then: yup.string().required(t("message.validation.requiredField"))
            })*/
        }),
        cbPersonalInfo: yup.object({
            firstName: yup.string().when([],{
                is: () => isPerson,
                then: yup.string().required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().notRequired()
            }),
            lastName: yup.string().when([],{
                is: () => isPerson,
                then: yup.string().required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().notRequired()
            }),
            birthDate: yup.string().when([],{
                is: () => isPerson,
                then: yup.string().required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().notRequired()
            }),
            businessName: yup.string().when([],{
                is: () => !isPerson,
                then: yup.string().required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().notRequired()
            }),
            vatNumberCountry: yup.string().nullable().when(['residenceCountry'], {
                is: (residenceCountry: string) => !isPerson && residenceCountry == CBCountry.ITALY && !NOT_REQUIRED_LEGAL_FORMS.includes(form.getValues("cbPersonalInfo.legalForm") || ''),
                then: yup.string().nullable().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().nullable().notRequired()
            }),
            vatNumber: yup.string().when([], {
                is: () => !isPerson && !NOT_REQUIRED_LEGAL_FORMS.includes(form.getValues("cbPersonalInfo.legalForm") || ''),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().notRequired().trim()
            }).test(
                CBYupTestID.CHECK_VAT_NUMBER_ON_UPDATE,
                (value, ctx) => {
                    const currValue = value == CBOmissionType.OMISSION_DASH ? '' : value
                    if(!isPerson && !!currValue) {
                        if(ctx.parent?.vatNumberCountry == CBCountry.ITALY)
                            return isVatNumberValid(currValue, CBCountry.ITALY) ? true : ctx.createError({message: t("message.validation.exactly11Numbers")});
                        
                        return isVatNumberValid(currValue) ? true : ctx.createError({message: t("message.validation.min5Chars")});
                    }

                    return true;
                }
            ),
            legalForm: yup.string().when([], {
                is: () => !isPerson,
                then: yup.string().required(t("message.validation.requiredField"))
            }),
            publicAuthority: yup.string().required(t("message.validation.requiredField")),
            paIdNumber: yup.string().when(['publicAuthority'], {
                is: (publicAuthority: string) => !isPerson && publicAuthority == CBYesNoType.YES,
                then: yup.string().required(t("message.validation.requiredField")).min(6, t("message.validation.exactly6Chars"))
            }),
            //birthCountry: yup.string().notRequired().trim(),
            /*residenceCountry: yup.string().nullable().when([], {
                is: () => isPerson,
                then: yup.string().nullable().required(t("message.validation.requiredField")).trim(),
                otherwise: yup.string().nullable().notRequired()
            }),*/
            residenceCountry: yup.string().nullable().required(t("message.validation.requiredField")).trim(),
            residenceProvince: yup.string().when(["residenceCountry"], {
                is: (residenceCountry: string) => !!residenceCountry && residenceCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField"))
            }),
            residenceCity: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            residenceZipCode: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            residenceStreet: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            residenceHouseNumber: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            taxCode: yup.string()
                .when(['residenceCountry'], {
                    is: (residenceCountry: string) => isPerson && [CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL].includes(residenceCountry as CBCountry),
                    then: yup
                        .string()
                        .required(t("message.validation.requiredField"))
                        .trim()
                        .uppercase()
                        .when(['residenceCountry'], {
                            is: (residenceCountry: string) => residenceCountry === CBCountry.ITALY,
                            then: yup.string().matches(
                                IT_FISCAL_CODE_FORMAT_REGEX,
                                {excludeEmptyString: true, message: t("message.validation.formatNotValid")}
                            ),
                        })
                        .when(['residenceCountry'], {
                            is: (residenceCountry: string) => residenceCountry === CBCountry.SPAIN,
                            then: yup.string().matches(
                                ES_FISCAL_CODE_FORMAT_REGEX,
                                {excludeEmptyString: true, message: t("message.validation.formatNotValid")}
                            ),
                        })
                        .when(['residenceCountry'], {
                            is: (residenceCountry: string) => residenceCountry === CBCountry.PORTUGAL,
                            then: yup.string().matches(
                                PT_FISCAL_CODE_FORMAT_REGEX,
                                {excludeEmptyString: true, message: t("message.validation.formatNotValid")}
                            ),
                        }),
                    otherwise: yup.string().notRequired().trim().uppercase()
                })
                .test(
                    CBYupTestID.CHECK_REQUIRED_AND_FORMAT_TAX_CODE_FOR_ORGANIZATION_ON_UPDATE, 
                    (value, ctx) => {
                        if(isPerson) {
                            const residenceCountry = ctx?.parent?.residenceCountry
                            if(!!residenceCountry && ![CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL, CBOmissionType.OMISSION_DASH].includes(residenceCountry)
                                && !!value && value !== CBOmissionType.OMISSION_DASH) {
                                    return isTaxCodeFormatValid(value, residenceCountry) ? true : ctx.createError({message: t("message.validation.formatNotValid")});
                            }
                        } else {
                            const residenceCountry = ctx?.parent?.residenceCountry
                            if([CBCountry.ITALY, CBCountry.SPAIN, CBCountry.PORTUGAL].includes(residenceCountry) && !value)
                                return ctx.createError({message: t("message.validation.requiredField")});

                            if(ctx.parent.legalForm == CBLegalFormKey.DITTA_INDIVIDUALE) {
                                return isTaxCodeFormatValid(value || null, residenceCountry) ? true : ctx.createError({message: t("message.validation.formatNotValid")});
                            }

                            /*if(value !== CBOmissionType.OMISSION_DASH)
                                return value.length >= CBValuableLength.MIN_VAT_NUMBER ? true : ctx.createError({message: t("message.validation.min5Chars")});*/
                        }

                        return true;

            }),
            /*birthProvince: yup.string().when(['birthCountry'], {
                is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                then: yup.string().trim().required(t("message.validation.requiredField")),
                otherwise: yup.string().notRequired().trim()
            }),
            birthCity: yup.string().when(['birthCountry'], {
                is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                then: yup.string().trim().required(t("message.validation.requiredField")),
                otherwise: yup.string().notRequired().trim()
            }),
            gender: yup.string().when(['birthCountry'], {
                is: (birthCountry: string) => isPerson && !!birthCountry && birthCountry == CBCountry.ITALY,
                then: yup.string().trim().required(t("message.validation.requiredField")),
                otherwise: yup.string().notRequired().trim()
            }),*/
            gender: yup.string().when([], {
                is: () => isPerson,
                then: yup.string().transform((value, _original) => handleYupTransform(value)).trim().required(t("message.validation.requiredField")),
                otherwise: yup.string().notRequired().trim()
            }),
            drvLicenceNumber: yup.string().when(['residenceCountry'], {
                is: (residenceCountry: string) => isPerson && !!residenceCountry && residenceCountry !== CBCountry.ITALY,
                then: yup.string().trim().required(t("message.validation.requiredField")),
                otherwise: yup.string().notRequired().trim()
            }),
            siren: yup.string().when(["vatNumberCountry"], {
                is: (vatNumberCountry: string) => !isPerson && !!vatNumberCountry && vatNumberCountry == CBCountry.FRANCE,
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim().min(CBValuableLength.SIREN, t("message.validation.exactly9Chars")).max(CBValuableLength.SIREN, t("message.validation.exactly9Chars")),
                otherwise: yup.string().notRequired().trim()
            }),
            siret: yup.string().when(["vatNumberCountry"], {
                is: (vatNumberCountry: string) => !isPerson && !!vatNumberCountry && vatNumberCountry == CBCountry.FRANCE,
                then: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim().matches(VALID_SIRET_FORMAT_REGEX, {excludeEmptyString: true, message: t("message.validation.exactly14Chars")}),
                otherwise: yup.string().notRequired().trim()
            })
            .test(
                CBYupTestID.IS_SIRET_AND_SIREN_COMPLIANT_ON_UPDATE,
                t("message.validation.sirenAndSiretNotCompliant"),
                (value, ctx) => {

                    const vatNumberCountry = handleYupTransform(ctx?.parent?.vatNumberCountry);
                    const siren = handleYupTransform(ctx?.parent?.siren);
                    const siret = handleYupTransform(value);

                    return (!isPerson && !!vatNumberCountry && vatNumberCountry == CBCountry.FRANCE && !!siret && !!siren && siret.length == CBValuableLength.SIRET && siren.length == CBValuableLength.SIREN) ?
                        siret.slice(0,CBValuableLength.SIREN) == siren : true;
                }
            )
        }),
        cbInvoicingAddress: yup.object().shape({
            invoicingStreet: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            invoicingHouseNumber: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            invoicingAddrEmail: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().required(t("message.validation.requiredField")).trim().lowercase().email(t("message.validation.emailNotValid")),
                otherwise: yup.string().notRequired().trim().lowercase().email(t("message.validation.emailNotValid"))
            }),
            invoicingZipCode: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            invoicingCountry: yup.string().nullable().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).nullable().required(t("message.validation.requiredField"))
            }),
            invoicingProvince: yup.string().when(["invoicingCountry"], {
                is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField"))
            }),
            invoicingCity: yup.string().when([], {
                is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim()
            }),
            invoicingPec: yup.string().when(["invoicingCountry"], {
                is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY,
                then: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim().lowercase().email(t("message.validation.emailNotValid")),
                otherwise: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim()
            }),
            invoicingRecipientCode: yup.string().when(["invoicingCountry"], {
                is: (invoicingCountry: string) => !!invoicingCountry && invoicingCountry == CBCountry.ITALY && form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && (form.getValues("cbPersonalInfo.publicAuthority") == CBYesNoType.NO),
                then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim().min(7, t("message.validation.exactly7Chars")),
                otherwise: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim()
            })
        }),
        cbPaymentInfo: yup.array().of(
            yup.object().shape({
                buagClass: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired(),
                }),
                paymentMethod: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired(),
                }),
                paymentTerms:  yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired(),
                }),
                correspondingVariant: yup.string().when([], {
                    is: () => form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY),
                    then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")),
                    otherwise: yup.string().notRequired(),
                }),
                iban: yup.string().when(['paymentMethod'], {
                    is: (paymentMethod: string) => paymentMethod == CBPaymentMethod.DIRECT_DEBIT,
                    then: yup.string().transform((value, _original) => handleYupTransform(value)).required(t("message.validation.requiredField")).trim().uppercase(),
                    otherwise: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim().uppercase()

                })
            })
        ).test(
            CBYupTestID.CHECK_FORMAT_IBAN_ON_UPDATE,
            (value, ctx) => {
                if(!!value) {
                    var err: any[] = [];
                    value.forEach((el, index) => {

                        var iban: string | undefined = el.iban;
                        if(!!iban) {

                            if(iban.length > 3) {
                                iban = iban.toUpperCase();
                                const countryCode = iban.substring(0,2);

                                if(countryCode == CBCountry.ITALY || countryCode == CBCountry.SAN_MARINO) {
                                    if(!iban.match(VALID_IT_SM_FORMAT_IBAN_REGEX))
                                        err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));
                                }
                                else if(!iban.match(VALID_GENERIC_FORMAT_IBAN_REGEX))
                                    err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));

                            }
                            else err.push(new yup.ValidationError(t("message.validation.formatNotValid"), value[index].iban,`${ctx.path}[${index}].iban`));
                        }

                    });

                    return err.length > 0 ? ctx.createError({message: () => err}) : true;
                }
                return true;
            }
        ).test(
            CBYupTestID.AT_LEAST_ONE_PAYMENT_REQUIRED,
            (value) => {
                if(form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && value?.length == 0) {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('message.validation.atLeastOnePaymentRequired'),
                                type: snackbarTypes.error,
                                show: true,
                                autoclose: true
                            }
                        }
                    });

                    return false;
                }
                return true;
            }
        ).test(
            CBYupTestID.BUAG_SALES_AREA_MARKET_CONSISTENCY_ON_UPDATE,
            (value, ctx) => {
                if(form.getValues("cbDetails.role").includes(CBRoleKey.SOLD_TO_PARTY) && !!value && (value?.length > 0) && (ctx.parent?.salesAreas?.length > 0)) {

                    const buagClasses: string[] = value.map(p => p?.buagClass || '');
                    const salesOrganizations: string[] = ctx.parent?.salesAreas?.map((sa: any) => sa?.salesOrganization);

                    let soUnique: string[] = [];
                    salesOrganizations.forEach(so => {
                        if(!soUnique.includes(so))
                            soUnique.push(so);
                    })

                    const bcFiltered = buagClasses.filter(bc => soUnique.includes(bc));
                    let bcfUnique: string[] = [];
                    bcFiltered.forEach(bcf => {
                        if(!bcfUnique.includes(bcf))
                            bcfUnique.push(bcf);
                    })

                    if(bcfUnique.length != soUnique.length) {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('message.validation.buagSalesAreaMarketConsistency'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoclose: true
                                }
                            }
                        });

                        return false;
                    }
                }

                return true;
            }
        ),
        salesAreas: yup.array().of(
            yup.object().shape({
                salesOrganization: yup.string().required(t("message.validation.requiredField")),
                distributionChannel: yup.string().required(t("message.validation.requiredField")),
                division: yup.string().required(t("message.validation.requiredField")),
                currency: yup.string().required(t("message.validation.requiredField"))
            })
        ),
        cbCorporateInfo: yup.object().when([], {
            is: () => !isPerson,
            then: yup.object().shape({
                    contactPersons: yup.array().of(yup.object().shape({
                        name: yup.string().required(t("message.validation.requiredField")).trim().max(CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH, t("message.validation.max20Chars")),
                        surname: yup.string().required(t("message.validation.requiredField")).trim().max(CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH, t("message.validation.max20Chars")),
                        email: yup.string().transform((value, _original) => handleYupTransform(value)).notRequired().trim().lowercase().email(t("message.validation.emailNotValid"))
                    })).test(
                        CBYupTestID.AT_LEAST_ONE_CONTACT_PERSON_REQUIRED_ON_UPDATE,
                        (value) => {
                            if(value?.length == 0) {
                                dispatchGlobalData({
                                    type: setSnackbar,
                                    payload: {
                                        snackbar: {
                                            message: t('message.validation.atLeastOneContactPersonRequired'),
                                            type: snackbarTypes.error,
                                            show: true,
                                            autoclose: true
                                        }
                                    }
                                });
        
                                return false;
                            }
                            return true;
                        })
                })
        })
    });
    
    const form = useForm<IFormValues>({
        defaultValues: {
            cbDetails: {
                cbCustomerId: '',
                bpCode: '',
                idTenant: '',
                oldBpCode: '',
                email: '',
                mobilePrefix: '',
                mobile: '',
                cbType: '',
                role: [],
                language: '',
                partnerType: '',
                vip: '',
                blacklist: '0',
                blacklistDescription: '',
                initialRegistrationDate: '',
                finalRegistrationDate: '',
                bpBlocked: '',
                idpUid: '',
                customerState: '',
                digitalIdentityStatus: '',
                extMgmtReason: ''
            },
            cbPersonalInfo: {
                firstName: '',
                lastName: '',
                gender: '',
                birthDate: '',
                birthCountry: '',
                birthProvince: '',
                birthCity: '',
                drvLicenceNumber: '',
                vatNumber: '',
                vatNumberCountry: '',
                businessName: '',
                taxCode: '',
                residenceStreet: '',
                residenceHouseNumber: '',
                residenceZipCode: '',
                residenceProvince: '',
                residenceCity: '',
                residenceCountry: '',
                residenceAddressType: CBAddressType.RESIDENCE,
                legalForm: '',
                dealerType: '',
                customerType: '',
                publicAuthority: CBYesNoType.NO,
                paIdNumber: '',
                cfValidated: '',
                siren: '',
                siret: ''
            },
            cbInvoicingAddress: {
                invoicingStreet: '',
                invoicingHouseNumber: '',
                invoicingZipCode: '',
                invoicingProvince: '',
                invoicingCity: '',
                invoicingCountry: '',
                invoicingAddrEmail: '',
                invoicingPec: '',
                invoicingAddrMobile: '',
                invoicingTelephone: '',
                invoicingFax: '',
                invoicingWebsite: '',
                invoicingCompanyFiscalCode: '',
                invoicingRecipientCode: '',
                invoicingAddressType: CBAddressType.INVOICING
            },
            termsAndConditions: [],
            salesAreas: [],
            cbPaymentInfo: [],
            cbExtraAddresses: [],
            csData: {
                sku: ''
            }
            //consents: []
        },
        //mode: "onChange",
        resolver: yupResolver(validationSchema)
    });

    const isPerson: boolean = form.getValues("cbDetails.partnerType") == CBPartnerType.PERSON;

    const sapEnumQuery = useQuery(['getSapEnumConfiguration'], getSapEnumConfiguration, 
    {
        enabled: false,   
        onSuccess: (data) => {
            //console.log("SAP: ", data?.data);
            setSapEnum(data?.data);
        },
        onError: (error) => {
            console.log("SAP Err: ", error);
        }
    });
    
    useEffect(()=> {
        if(!Object.values(sapEnum).some(val => val.length > 0)) 
            sapEnumQuery.refetch();
    }, [sapEnum]);

    const mobilePrefixQuery = useQuery(['getMobilePrefix'], getMobilePrefix, {
        enabled: !!mobilePrefixes && (mobilePrefixes.length == 0),
        onSuccess: (data) => {
            //console.log("Mobile Prefix: ", data?.data);
            setMobilePrefix(data?.data)

        },
        onError: (error) => {
            console.log("Mobile Prefix Err: ", error);

        }
    });

    const getCountriesQuery = useQuery(['getCountries'], getCountries, {
        enabled: !!countryOptions && (countryOptions.length == 0),
        onSuccess: (data) => {
            setCountryOptions(data.data?.data || []);
        },
        onError: (err: any) => {
            console.log(err)
        }

    });

    const getProvincesQuery = useQuery(['getProvinces'], getProvinces, {
        enabled: !!provinceOptions && (provinceOptions.length == 0),
        onSuccess: (data) => {
            const provinces: IGetProvincesReturn = data?.data;
            
            if(!!provinces && !!provinces.data && provinces.data.length>0)
                setProvinceOptions(provinces.data.map((p) => ({
                    country: p.country || '',
                    codeProvince: p.codeProvince || '',
                    description: p.description || ''
                })) || []);

        },
        onError: (err: any) => {
            setProvinceOptions([]);
        }

    });

    const getCargosPlacesQuery = useQuery(['getCargosPlaces'], {
        enabled: !!cargosPlaceOptions && (cargosPlaceOptions.length == 0),
        queryFn: () => {
            return getCargosPlaces();
        },
        onSuccess: (data) => {
            const places: IGetCargosPlaceReturn = data?.data;

            if(!!places && !!places.data && places.data.length>0)
                setCargosPlaceOptions(places.data.map((cp) => ({
                    codice: cp.codice || '',
                    descrizione: cp.descrizione || '',
                    provincia: cp.provincia || '',
                    dataFineVal: cp.dataFineVal || null
                })) || []);
        },
        onError: (err: any) => {
            setCargosPlaceOptions([]);
        }
    });

    useEffect(() => {
        setErrorSection({
            cbDetails: false,
            cbPersonalOrganizationInfo: false,
            cbInvoicingAddress: false,
            salesAreas: false,
            cbPaymentInfo: false,
            cbCorporateInfo: false
        });

        if(!!onCustomerSubmit && isCreationMode) setIsMobilityVoucherMode(true);

    }, [])

    /** QUANDO PASSO DAI DETAILS A CREATE RIPULISCO LO STATE DEL CUSTOMER */
    useEffect(()=> {

        if(!isCreationMode) return;

        setCustomerSearchFilters(null);
        /** Se i valori e le logiche sono uguali tra i vari array
         * allora si può valutare di usare una sola variabile recoil
        */
        setDefaultSalesAreas([]);
        setDefaultBuagClasses([]);
        /** */

        form.reset({
            cbDetails: {
                cbCustomerId: '',
                bpCode: '',
                idTenant: '',
                oldBpCode: '',
                email: '',
                mobilePrefix: '',
                mobile: '',
                cbType: '',
                role: isMobilityVoucherMode ? [CBRoleKey.PROSPECT, CBRoleKey.SOLD_TO_PARTY] : [CBRoleKey.PROSPECT],
                language: '',
                partnerType: isOrganizationCreation? CBPartnerType.ORGANIZATION : CBPartnerType.PERSON,
                vip: '',
                blacklist: '0',
                blacklistDescription: '',
                initialRegistrationDate: '',
                finalRegistrationDate: '',
                bpBlocked: '',
                idpUid: '',
                customerState: '',
                digitalIdentityStatus: '',
                extMgmtReason: ''
            },
            cbPersonalInfo: {
                firstName: '',
                lastName: '',
                gender: '',
                birthDate: '',
                birthCountry: '',
                birthProvince: '',
                birthCity: '',
                drvLicenceNumber: '',
                vatNumber: '',
                vatNumberCountry: '',
                businessName: '',
                taxCode: '',
                residenceStreet: '',
                residenceHouseNumber: '',
                residenceZipCode: '',
                residenceProvince: '',
                residenceCity: '',
                residenceCountry: '',
                residenceAddressType: CBAddressType.RESIDENCE,
                legalForm: '',
                dealerType: '',
                customerType: '',
                publicAuthority: CBYesNoType.NO,
                paIdNumber: '',
                cfValidated: '',
                siren: '',
                siret: ''
            },
            cbInvoicingAddress: {
                invoicingStreet: '',
                invoicingHouseNumber: '',
                invoicingZipCode: '',
                invoicingProvince: '',
                invoicingCity: '',
                invoicingCountry: '',
                invoicingAddrEmail: '',
                invoicingPec: '',
                invoicingAddrMobile: '',
                invoicingTelephone: '',
                invoicingFax: '',
                invoicingWebsite: '',
                invoicingCompanyFiscalCode: '',
                invoicingRecipientCode: isMobilityVoucherMode ? CBInvoicingCode.DEFAULT : '',
                invoicingAddressType: CBAddressType.INVOICING
            },
            termsAndConditions: [],
            salesAreas: [{
                salesOrganization: getSalesOrganizationKeyByTenants(globalData?.user?.legalEntities),
                distributionChannel: "Direct",
                division: "ST",
                currency: "EUR"
            }],
            cbPaymentInfo: [],
            cbCorporateInfo: {
                salesArea: '',
                fleet: '',
                class: '',
                sizingClass: '',
                maxRentalNum: '',
                contactPersons: [{
                    name: "",
                    surname: "",
                    address: "",
                    houseNumber: "",
                    country: "",
                    region: "",
                    city: "",
                    postalCode: "",
                    phoneNumber: "",
                    mobile: "",
                    email: "",
                    delete: "" 
                }]
            },
            cbConsents: [],
            cbRegistryPhases: [],
            cbDossiers: [],
            csData: {}
        });

        setCurrentCB(null);
        
    }, [isCreationMode, isOrganizationCreation, isPersonCreation, isMobilityVoucherMode]);

    const onSuccess = (data: any) => {
        const customerbase: CustomerBaseDto = data?.data?.data;
        
        if(!customerbase) return;

        var consents: any[] = [];
        if(customerbase?.termsAndCond && sapEnum.consentTypes) {
            consents = customerbase.termsAndCond
            .filter((tc) => sapEnum.consentTypes.find((ct) => tc.termCondType == ct.key))
            .map((consent) => ({
                id: consent.id || null,
                companyCode: consent.companyCode || null,
                termCondType: consent.termCondType || CBOmissionType.OMISSION_DASH,
                result: consent.result,
                division: consent.division || CBOmissionType.OMISSION_DASH,
                communicationChannel: consent.communicationChannel || CBOmissionType.OMISSION_DASH,
                distributionChannel: consent.distributionChannel || CBOmissionType.OMISSION_DASH,
                revocationChannel: consent.revocationChannel || CBOmissionType.OMISSION_DASH,
                revocationReason: consent.revocationReason || CBOmissionType.OMISSION_DASH,
                acquisitionDateTime: consent.acquisitionDateTime || CBOmissionType.OMISSION_DASH,
                revocationDateTime: consent.revocationDateTime || CBOmissionType.OMISSION_DASH
            }));

            sapEnum.consentTypes.forEach(({key}) => {
                if(!customerbase.termsAndCond?.find((tc) => key == tc.termCondType)){
                    consents.push({
                        id: null,
                        companyCode: null,
                        termCondType: key,
                        result: null,
                        division: CBOmissionType.OMISSION_DASH,
                        communicationChannel: CBOmissionType.OMISSION_DASH,
                        distributionChannel: CBOmissionType.OMISSION_DASH,
                        revocationChannel: CBOmissionType.OMISSION_DASH,
                        revocationReason: CBOmissionType.OMISSION_DASH,
                        acquisitionDateTime: CBOmissionType.OMISSION_DASH,
                        revocationDateTime: CBOmissionType.OMISSION_DASH
                    });
                }

            });
        }

        //console.log("GROGU: ", consents);

        const residenceAddress = getResidenceAddress(customerbase?.contactInfo);

        const invoicingAddress = getInvoicingAddress(customerbase?.contactInfo);

        form.reset({ 
            cbDetails: {
                cbCustomerId: customerbase?.cbCustomerId || CBOmissionType.OMISSION_DASH,
                bpCode: customerbase?.bpCode || CBOmissionType.OMISSION_DASH,
                idTenant: customerbase?.idTenant || CBOmissionType.OMISSION_DASH,
                oldBpCode: customerbase?.oldBpCode || CBOmissionType.OMISSION_DASH,
                email: customerbase?.email || CBOmissionType.OMISSION_DASH,
                mobilePrefix: customerbase?.contactInfo?.prefix || CBOmissionType.OMISSION_DASH,
                mobile: customerbase?.contactInfo?.mobile || CBOmissionType.OMISSION_DASH,
                cbType: customerbase?.cbType || CBOmissionType.OMISSION_DASH,
                role: customerbase?.role || [CBOmissionType.OMISSION_DASH],
                language: customerbase?.language || CBOmissionType.OMISSION_DASH,
                partnerType: customerbase?.partnerType || CBOmissionType.OMISSION_DASH,
                vip: customerbase?.vip || CBOmissionType.OMISSION_DASH,
                blacklist: customerbase?.blacklist ? String(customerbase.blacklist) || '0' : '0',
                blacklistDescription: customerbase?.blacklistDescription || CBOmissionType.OMISSION_DASH,
                initialRegistrationDate: customerbase?.initialRegDate || CBOmissionType.OMISSION_DASH,
                finalRegistrationDate: customerbase?.finalRegDate || CBOmissionType.OMISSION_DASH,
                bpBlocked: customerbase?.bpBlocked || CBOmissionType.OMISSION_DASH,
                idpUid: customerbase?.idpUid || CBOmissionType.OMISSION_DASH,
                customerState: customerbase?.customerState || '',
                digitalIdentityStatus: customerbase?.digitalIdentityStatus || '',
                extMgmtReason: customerbase?.extMgmtReason || ''
            },
            cbPersonalInfo: {
                firstName: customerbase?.registry?.firstName || CBOmissionType.OMISSION_DASH,
                lastName: customerbase?.registry?.lastName || CBOmissionType.OMISSION_DASH,
                gender: customerbase?.registry?.gender || CBOmissionType.OMISSION_DASH,
                birthDate: customerbase?.registry?.birthDate || CBOmissionType.OMISSION_DASH,
                birthCountry: customerbase?.registry?.birthCountry || CBOmissionType.OMISSION_DASH,
                birthProvince: customerbase?.registry?.birthProvince || CBOmissionType.OMISSION_DASH,
                birthCity: customerbase?.registry?.birthCity || CBOmissionType.OMISSION_DASH,
                drvLicenceNumber: customerbase?.registry?.drvLicenceNumber || CBOmissionType.OMISSION_DASH,
                vatNumber: customerbase?.registry?.vatNumber || CBOmissionType.OMISSION_DASH,
                vatNumberCountry: customerbase?.registry?.vatNumberCountry || CBOmissionType.OMISSION_DASH,
                businessName: customerbase?.registry?.businessName || CBOmissionType.OMISSION_DASH,
                taxCode: customerbase?.registry?.taxCode || CBOmissionType.OMISSION_DASH,
                residenceAddressId: residenceAddress?.id || null,
                residenceAddressNumber: residenceAddress?.addressNumber || null,
                residenceStreet: residenceAddress?.street || CBOmissionType.OMISSION_DASH,
                residenceHouseNumber: residenceAddress?.houseNumber || CBOmissionType.OMISSION_DASH,
                residenceZipCode: residenceAddress?.zipCode || CBOmissionType.OMISSION_DASH,
                residenceProvince: residenceAddress?.province || CBOmissionType.OMISSION_DASH,
                residenceCity: residenceAddress?.city || CBOmissionType.OMISSION_DASH,
                residenceCountry: residenceAddress?.country || CBOmissionType.OMISSION_DASH,
                residenceAddressType: residenceAddress?.addressType || CBAddressType.RESIDENCE,
                legalForm: customerbase?.legalForm || CBOmissionType.OMISSION_DASH,
                dealerType: customerbase?.dealerType || CBOmissionType.OMISSION_DASH,
                customerType: customerbase?.customerType || CBOmissionType.OMISSION_DASH,
                publicAuthority: customerbase?.publicAuthority || CBYesNoType.NO,
                paIdNumber: customerbase?.paIdNumber || "",
                cfValidated: customerbase?.cfValidated || '',
                siren: customerbase?.registry?.siren || CBOmissionType.OMISSION_DASH,
                siret: customerbase?.registry?.siret || CBOmissionType.OMISSION_DASH,
            },
            cbInvoicingAddress: {
                invoicingAddressId: invoicingAddress?.id || null,
                invoicingAddressNumber: invoicingAddress?.addressNumber || null,
                invoicingStreet: invoicingAddress?.street || CBOmissionType.OMISSION_DASH,
                invoicingHouseNumber: invoicingAddress?.houseNumber || CBOmissionType.OMISSION_DASH,
                invoicingZipCode: invoicingAddress?.zipCode || CBOmissionType.OMISSION_DASH,
                invoicingProvince: invoicingAddress?.province || CBOmissionType.OMISSION_DASH,
                invoicingCity: invoicingAddress?.city || CBOmissionType.OMISSION_DASH,
                invoicingCountry: invoicingAddress?.country || CBOmissionType.OMISSION_DASH,
                invoicingAddrEmail: invoicingAddress?.addrEmail || CBOmissionType.OMISSION_DASH,
                invoicingPec: invoicingAddress?.pec || CBOmissionType.OMISSION_DASH,
                invoicingAddrMobile: invoicingAddress?.addrMobile || CBOmissionType.OMISSION_DASH,
                invoicingTelephone: invoicingAddress?.telephone || CBOmissionType.OMISSION_DASH,
                invoicingFax: invoicingAddress?.fax || CBOmissionType.OMISSION_DASH,
                invoicingWebsite: invoicingAddress?.website || CBOmissionType.OMISSION_DASH,
                invoicingCompanyFiscalCode: invoicingAddress?.companyFiscalCode || CBOmissionType.OMISSION_DASH,
                invoicingRecipientCode: invoicingAddress?.recipientCode || CBOmissionType.OMISSION_DASH,
                invoicingAddressType: invoicingAddress?.addressType || CBAddressType.INVOICING
            },
            termsAndConditions: [], /*customerbase?.termsAndCond && sapEnum.consentTypes ? customerbase.termsAndCond
            .filter((tc) => !sapEnum.consentTypes.find((ct) => tc.termCondType == ct.key))
            .map(tc => ({
                id: tc.id || null,
                companyCode: tc.companyCode || null,
                termCondType: tc.termCondType || "--",
                result: tc.result,
                division: tc.division || "--",
                communicationChannel: tc.communicationChannel || "--",
                distributionChannel: tc.distributionChannel || "--",
                revocationChannel: tc.revocationChannel || "--",
                revocationReason: tc.revocationReason || "--",
                acquisitionDateTime: tc.acquisitionDateTime || "--",
                revocationDateTime: tc.revocationDateTime || "--"  
            }) || []) : [],*/ //AL MOMENTO SI È DECISO DI NON TRATTARE I TC MA SOLO I CONSENSI
            salesAreas: customerbase?.salesAreas ? customerbase.salesAreas?.map(sa => ({
                id: sa.id || null,
                salesOrganization: sa.salesOrganization || CBOmissionType.OMISSION_DASH,
                distributionChannel: sa.distributionChannel || CBOmissionType.OMISSION_DASH,
                division: sa.division || CBOmissionType.OMISSION_DASH,
                currency: sa.currency || CBOmissionType.OMISSION_DASH
            })) : [],
            cbPaymentInfo: customerbase?.paymentInfo ? customerbase?.paymentInfo?.map(pi => ({
                id: pi.id || null,
                buagId: pi.buagId || null,
                buagClass: pi.buagClass || CBOmissionType.OMISSION_DASH,
                paymentMethod: pi.paymentMethod || CBOmissionType.OMISSION_DASH,
                paymentTerms: pi.paymentTerms || CBOmissionType.OMISSION_DASH,
                correspondingVariant: pi.correspondingVariant || CBOmissionType.OMISSION_DASH,
                iban: pi.iban || CBOmissionType.OMISSION_DASH,
                accountHolder: pi.accountHolder || CBOmissionType.OMISSION_DASH,
                referenceDetails: pi.referenceDetails || CBOmissionType.OMISSION_DASH

            })) : [],
            cbCorporateInfo: {
                idZIT019: customerbase.corporateInfo?.idZIT019 || null,
                salesArea: customerbase.corporateInfo?.salesArea || CBOmissionType.OMISSION_DASH,
                fleet: customerbase.corporateInfo?.fleet || CBOmissionType.OMISSION_DASH,
                class: customerbase.corporateInfo?.class || CBOmissionType.OMISSION_DASH,
                sizingClass: customerbase.corporateInfo?.sizingClass || CBOmissionType.OMISSION_DASH,
                maxRentalNum: customerbase.corporateInfo?.maxRentalNum || "",
                contactPersons: customerbase.corporateInfo?.contactPersons ? customerbase.corporateInfo.contactPersons.map(cp => ({
                    id: cp.id || null,
                    //cbCustomerId: cp.cbCustomerId || null,
                    bpCode: cp.bpCode || null,
                    name: cp.name !== CBOmissionType.OMISSION_DASH ? cp.name?.slice(0, CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH) || CBOmissionType.OMISSION_DASH : CBOmissionType.OMISSION_DASH,
                    surname: cp.surname !== CBOmissionType.OMISSION_DASH ? cp.surname?.slice(0, CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH) || CBOmissionType.OMISSION_DASH : CBOmissionType.OMISSION_DASH,
                    address: cp.address || CBOmissionType.OMISSION_DASH,
                    houseNumber: cp.houseNumber || CBOmissionType.OMISSION_DASH,
                    country: cp.country || CBOmissionType.OMISSION_DASH,
                    region: cp.region || CBOmissionType.OMISSION_DASH,
                    city: cp.city || CBOmissionType.OMISSION_DASH,
                    postalCode: cp.postalCode || CBOmissionType.OMISSION_DASH,
                    phoneNumber: cp.phoneNumber || CBOmissionType.OMISSION_DASH,
                    mobile: cp.mobile || CBOmissionType.OMISSION_DASH,
                    email: cp.email || CBOmissionType.OMISSION_DASH,
                    delete: cp.delete || CBOmissionType.OMISSION_DASH
                })) : []
            },
            cbConsents: consents,
            cbRegistryPhases: customerbase.registryPhases? customerbase.registryPhases.map((rp) => ({
                customerId: rp.customerId || null,
                phaseType: rp.phaseType,
                phaseCaption: rp.phaseCaption,
                verify: rp.verify,
                subState: rp.subState
            })) : [],
            cbDossiers: customerbase.dossiers ? customerbase.dossiers.map((ds) => ({
                idDossier: ds.idDossier || null,
                customerId: ds.customerId || null,
                title: ds.title,
                description: ds.description,
                creationTime: ds.creationTime,
                updateTime: ds.updateTime,
                identifier: ds.identifier,
                status: ds.status,
                origin: ds.origin
            })) : [],
            cbBundles: customerbase.bundles ? customerbase.bundles.map((bs) => ({
                idBundle: bs.idBundle || null,
                uuid: bs.uuidBundle || null,
                country: bs.country
            })) : [],
            cbExtraAddresses: customerbase.linkedAddresses ? customerbase.linkedAddresses.map((elem) => ({
                idAssociation: elem.idAssociation || null,
                customerId: elem.customerId || null,
                bpCode: elem.bpCode || null,
                division: elem.division || CBOmissionType.OMISSION_DASH,
                businessName: elem.businessName || CBOmissionType.OMISSION_DASH,
                vatNumber: elem.vatNumber || CBOmissionType.OMISSION_DASH,
                firstName: elem.firstName || CBOmissionType.OMISSION_DASH,
                lastName: elem.lastName || CBOmissionType.OMISSION_DASH,
                taxCode: elem.taxCode || CBOmissionType.OMISSION_DASH,
                addressNumber: elem.addressNumber || CBOmissionType.OMISSION_DASH,
                street: elem.street || CBOmissionType.OMISSION_DASH,
                houseNumber: elem.houseNumber || CBOmissionType.OMISSION_DASH,
                zipCode: elem.zipCode || CBOmissionType.OMISSION_DASH,
                province: elem.province || CBOmissionType.OMISSION_DASH,
                city: elem.city || CBOmissionType.OMISSION_DASH,
                country: elem.country || CBOmissionType.OMISSION_DASH,
                addrEmail: elem.addrEmail || CBOmissionType.OMISSION_DASH,
                pec: elem.pec || CBOmissionType.OMISSION_DASH,
                addrMobile: elem.addrMobile || CBOmissionType.OMISSION_DASH,
                telephone: elem.telephone || CBOmissionType.OMISSION_DASH,
                fax: elem.fax || CBOmissionType.OMISSION_DASH,
                website: elem.website || CBOmissionType.OMISSION_DASH,
                companyFiscalCode: elem.companyFiscalCode || CBOmissionType.OMISSION_DASH,
                recipientCode: elem.recipientCode || CBOmissionType.OMISSION_DASH,
                addressType: elem.addressType || CBOmissionType.OMISSION_DASH,
                product: elem.product || CBOmissionType.OMISSION_DASH,
                productDetail: elem.productDetail || CBOmissionType.OMISSION_DASH,
                registration: elem.registration || CBOmissionType.OMISSION_DASH
            })) : [],
            csData: customerbase?.csData || {sku: '' }
        });

        setCurrentCB({...customerbase, businessLines: [
            {
                description: "SHORT_TERM",
                status: "0"
            },
            {
                description: "CAR_SHARING",
                status: "1"
            },
            {
                description: "SUBSCRIPTION",
                status: "2"
            },
            
            {
                description: "LONG_TERM",
                status: "0"
            }
        ]});

        setDefaultSalesAreas([]);
        setDefaultBuagClasses([]);

    };

    const onError=(error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.default'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
        //setCurrentCB(null);

    };

    const { isFetching, refetch } = useQuery(['getCustomerBaseById', cbId], 
    {
        enabled: !!cbId && !isCreationMode && (sapEnum.consentTypes.length > 0),
        queryFn: () => {
            if(!!cbId && !isCreationMode)
                return getCustomerBaseById(cbId);
        },
        onSuccess,
        onError
    });

    /*useEffect(() => {
        if(!!cbId && !isCreationMode && (sapEnum.consentTypes.length > 0))
                refetch();

    }, [cbId, isCreationMode, sapEnum]);*/

    const customerbaseUpdateQuery = useQuery(['editCustomerbaseFields', {queryFields}], editCustomerbaseFields, {
        enabled: false,
        onSuccess: (data: any) => {

            const isUpdateWithExtraAdressProblems = !!data?.data?.code && data?.data?.code == UPDATE_SUCCESS_WITH_EXTRA_ADDRESS_PROBLEM_CODE;
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: isUpdateWithExtraAdressProblems ? t(`message.warning.${UPDATE_SUCCESS_WITH_EXTRA_ADDRESS_PROBLEM_CODE}`) : t("message.success.default"),
                        type: isUpdateWithExtraAdressProblems ? snackbarTypes.warning : snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            /** Edit a Sezioni */
            //if(!isCreationMode) changeCBEditButtonTriggerStatus(CBEditButtonTriggerStatus.UPDATED);
            /** */
            /** Logiche per la Global Edit */
            if(!isCreationMode){ 
                setIsGlobalEditMode(false);
                refetch();
            }
            /** */
            /**Logiche per eliminare i Contact Person */
            if(cbContactPersonToDeleteArray.length > 0)
                setCbContactPersonToDeleteArray([]);

            if(isCreationMode && !!data?.data?.data?.cbCustomerId && !isMobilityVoucherMode)
                navigate(`/customerbase/view/${data.data.data.cbCustomerId}`);

            setQueryFields(undefined);
        },
        onError: (error:any) => {
            console.log(error);

            const code: string = getErrorKey(error);
            const sapMessage: string =  error?.response?.data?.message || '';
            const isSapKO: boolean = code == CBSapCode.KO_UPDATE;
            
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: isSapKO ? t(`message.warning.${code}`)?.concat(!!sapMessage ? ": [" + `${sapMessage}]` : '') : t(`message.error.${code}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            //if(!isCreationMode) changeCBEditButtonTriggerStatus(CBEditButtonTriggerStatus.UNDEFINED);
        }
    });

    useEffect(() => {

        if(!!queryFields) {
            customerbaseUpdateQuery.refetch();
        }   

    }, [queryFields]);

    return (
        <Box>
            <FormProvider {...form}>
                <CBSectionHeader creationOf={creationOf} customer={currentCB}/>            
                {!isCreationMode &&  <ActionsBar />}
                <CustomerBaseTabsManager creationOf={creationOf} onCustomerSubmit={onCustomerSubmit}/>
            </FormProvider>
            <CustomerBaseModal 
                open={openModal} 
                contentDescription={t("editingModal.descriptionWarning")}
                buttonCloseDescription={t("editingModal.buttons.close")}
                buttonConfirmDescription={t("editingModal.buttons.confirm")}
                onClose={() => {setOpenModal(false)}}
                onConfirm={async () => {
                    await form.reset();
                    
                    /** Logiche per la Global Edit */
                    setErrorSection({
                        cbDetails: false,
                        cbPersonalOrganizationInfo: false,
                        cbInvoicingAddress: false,
                        salesAreas: false,
                        cbPaymentInfo: false,
                        cbCorporateInfo: false
                    });
                    setIsGlobalEditMode(false);
                    /** */

                    setOpenModal(false);
                    /** Edit a Sezioni */
                    //setAnotherSectionIsInEdit(false);
                    /** */
                    
                }}
            />
            <CustomLoader loading={isFetching || sapEnumQuery.isFetching || mobilePrefixQuery.isFetching ||  getCountriesQuery.isFetching || getProvincesQuery.isFetching || getCargosPlacesQuery.isFetching || customerbaseUpdateQuery.isFetching }/>
        </Box>
    );
}

export default ViewCustomerBase;